// Pure calculations for the Expense Budgets screen.
// Mirrors api/src/utils/expense-budget.ts; keep both in sync.
import { BillingFrequency, ExpenseBudget, ExpenseBudgetCurrency, ExpenseBudgetLine } from "models/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

export interface AlertThreshold {
    percent: number
    amount: number
}

export const sumAmounts = (values: number[]): number =>
    Math.round(values.reduce((total, value) => total + value, 0) * 100) / 100

export const rateToCad = (budget: ExpenseBudget, currency: ExpenseBudgetCurrency): number => {
    if (currency === ExpenseBudgetCurrency.CAD) return 1
    const rate = budget.exchangeRates.find(r => r.currency === currency)
    return rate ? Number(rate.rateToCad) : 0
}

// One bill in CAD, tax included.
export const lineChargeCad = (budget: ExpenseBudget, line: ExpenseBudgetLine): number =>
    Number(line.quantity) * Number(line.unitPrice) * rateToCad(budget, line.currency) * (1 + Number(line.taxRate))

// Quarterly and annual bills are prepaid, so accounting spreads them evenly over the year.
export const isAmortizedFrequency = (frequency: BillingFrequency): boolean =>
    frequency === BillingFrequency.Annually || frequency === BillingFrequency.Quarterly

// Annual and quarterly products are billed on their renewal date.
export const isRenewalBased = isAmortizedFrequency

// The fiscal period of the renewal month, within the budget's fiscal year.
export const billingPeriodFromRenewal = (renewalDate: string, fiscalYear: number): number => {
    const { period } = calendar.dateToFiscal(new Date(`${renewalDate.slice(0, 10)}T12:00:00`))
    return fiscalYear * 100 + period
}

// How many bills land in the fiscal year, counting from the first billing period (1–12).
export const billsInYear = (frequency: BillingFrequency, startPeriod: number): number => {
    switch (frequency) {
        case BillingFrequency.Monthly: return 13 - startPeriod
        case BillingFrequency.Quarterly: return 4
        default: return 1
    }
}

export const lineAnnualCad = (line: ExpenseBudgetLine): number =>
    sumAmounts(line.periods.map(period => Number(period.cashAmount)))

// Over plan only when BOTH the percentage and the dollar amount are exceeded.
export const isOverThreshold = (planned: number, actual: number, threshold: AlertThreshold): boolean => {
    const overAmount = actual - planned
    if (overAmount <= threshold.amount) return false
    if (planned <= 0) return true
    return (overAmount / planned) * 100 > threshold.percent
}

export const budgetThreshold = (budget: ExpenseBudget): AlertThreshold => ({
    percent: Number(budget.alertPercentThreshold),
    amount: Number(budget.alertAmountThreshold),
})

interface TreeDepartment {
    id: string
    code: string
    description: string
    parentId: string | null
}

const INDENT = "    "

// Dropdown options in hierarchy order: each department followed by its children, siblings sorted by
// name, children indented. Departments whose parent isn't in the list are shown as top level.
export const departmentTreeOptions = (departments: TreeDepartment[]): { value: string; label: string }[] => {
    const ids = new Set(departments.map(d => d.id))
    const byName = (a: TreeDepartment, b: TreeDepartment) => a.description.localeCompare(b.description)
    const childrenOf = (parentId: string | null) => departments
        .filter(d => (parentId === null ? !d.parentId || !ids.has(d.parentId) : d.parentId === parentId))
        .sort(byName)

    const walk = (parentId: string | null, depth: number): { value: string; label: string }[] =>
        childrenOf(parentId).flatMap(d => [
            { value: d.id, label: `${INDENT.repeat(depth)}${d.description} (${d.code})` },
            ...walk(d.id, depth + 1),
        ])

    return walk(null, 0)
}

// Rates are stored as fractions (0.07) and shown as percentages (7).
export const toPercent = (fraction: number | string): number => Math.round(Number(fraction) * 10000) / 100
export const fromPercent = (percent: number): number => percent / 100

// Splits a bill amount across products in proportion to their weights (e.g. each product's plan in
// the bill's month), rounded to cents with the rounding remainder on the last product. With no
// positive weight, the amount is split equally.
export const splitByPlan = (total: number, weights: number[]): number[] => {
    if (weights.length === 0) return []
    const positive = weights.map(weight => Math.max(weight, 0))
    const weightTotal = positive.reduce((acc, weight) => acc + weight, 0)
    const shares = weightTotal > 0 ? positive.map(weight => weight / weightTotal) : weights.map(() => 1 / weights.length)
    const amounts = shares.slice(0, -1).map(share => Math.round(total * share * 100) / 100)
    return [...amounts, Math.round((total - amounts.reduce((acc, amount) => acc + amount, 0)) * 100) / 100]
}

// Weight of each product for a bill split: its plan in the bill's month (Cash view), or its yearly
// plan when none of the products has a plan that month.
export const planWeights = (products: ExpenseBudgetLine[], fiscalPeriod: number): number[] => {
    const monthly = products.map(product =>
        Number(product.periods.find(p => p.fiscalPeriod === fiscalPeriod)?.cashAmount ?? 0))
    if (monthly.some(weight => weight > 0)) return monthly
    return products.map(product => sumAmounts(product.periods.map(p => Number(p.cashAmount))))
}
